import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bot, Send, X, Sparkles, GripHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("naevo_ai_sid") || null;
  });
  const [messages, setMessages] = useState([
    { role: "assistant", text: "hi, i'm Nia — your trading copilot. ask me anything about the arcade, charts, smart recovery or your current session." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    try { return JSON.parse(localStorage.getItem("naevo_ai_pos")) || { x: 0, y: 0 }; } catch { return { x: 0, y: 0 }; }
  });
  const dragging = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Collect current session context from localStorage so the AI knows the user state
  const buildContext = () => {
    try {
      const ctx = {};
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      ctx.page = path || "/";
      const b = localStorage.getItem("naevo_balance");
      if (b) ctx.balance = `$${b}`;
      const br = localStorage.getItem("naevo_bankroll");
      if (br) ctx.bankroll = `$${br}`;
      const top = localStorage.getItem("naevo_top_pair");
      if (top) ctx.top_trending_pair = top;
      const streak = localStorage.getItem("naevo_streak");
      if (streak) ctx.recent_streak = streak;
      const lastP = localStorage.getItem("naevo_last_pnl");
      if (lastP) ctx.last_round_pnl = `$${lastP}`;
      return ctx;
    } catch { return {}; }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionId, context: buildContext() });
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem("naevo_ai_sid", data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "sorry — i hit an error. try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const onDragStart = (e) => {
    const point = e.touches ? e.touches[0] : e;
    dragging.current = { startX: point.clientX, startY: point.clientY, baseX: pos.x, baseY: pos.y };
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
    window.addEventListener("touchmove", onDragMove, { passive: false });
    window.addEventListener("touchend", onDragEnd);
  };
  const onDragMove = (e) => {
    if (!dragging.current) return;
    const point = e.touches ? e.touches[0] : e;
    if (e.cancelable) e.preventDefault?.();
    const nx = dragging.current.baseX + (point.clientX - dragging.current.startX);
    const ny = dragging.current.baseY + (point.clientY - dragging.current.startY);
    const maxX = (typeof window !== "undefined" ? window.innerWidth : 1024) - 420;
    const maxY = (typeof window !== "undefined" ? window.innerHeight : 768) - 120;
    setPos({
      x: Math.min(0, Math.max(-maxX, nx)),
      y: Math.min(0, Math.max(-maxY, ny)),
    });
  };
  const onDragEnd = () => {
    if (!dragging.current) return;
    dragging.current = null;
    try { localStorage.setItem("naevo_ai_pos", JSON.stringify(pos)); } catch {}
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
    window.removeEventListener("touchmove", onDragMove);
    window.removeEventListener("touchend", onDragEnd);
  };

  const suggestions = [
    "what's trending now?",
    "which recovery cap should i use?",
    "give me a safe stake idea",
  ];

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-assistant-toggle"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full btn-magenta flex items-center justify-center shadow-xl pulse-glow"
        aria-label="Open AI assistant"
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ type: "spring", damping: 22 }}
            className="fixed bottom-24 right-6 z-50 w-[min(380px,calc(100vw-32px))] max-h-[70vh] rounded-2xl border border-white/10 bg-[#0C0914]/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
            style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
            data-testid="ai-assistant-panel"
          >
            {/* drag handle */}
            <div
              onMouseDown={onDragStart}
              onTouchStart={onDragStart}
              className="flex items-center justify-between px-4 py-3 border-b border-white/5 cursor-grab active:cursor-grabbing select-none"
              data-testid="ai-drag-handle"
            >
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-magenta/15 border border-magenta/40 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-magenta" />
                </div>
                <div>
                  <div className="font-display text-base flex items-center gap-2">Nia <GripHorizontal className="h-3 w-3 text-white/30" /></div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Naevo copilot · drag me</div>
                </div>
              </div>
              <div className="text-xs text-magenta font-mono-naevo">● online</div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] text-sm px-3 py-2 rounded-xl whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-magenta/15 border border-magenta/30 text-white rounded-br-sm"
                        : "bg-white/[0.04] border border-white/10 text-white/90 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-1 pl-3">
                  <span className="h-2 w-2 bg-magenta rounded-full animate-bounce" />
                  <span className="h-2 w-2 bg-magenta rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                  <span className="h-2 w-2 bg-magenta rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                </div>
              )}
            </div>
            {messages.length < 2 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="text-xs rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-white/70 hover:border-magenta/50 hover:text-magenta transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="border-t border-white/5 p-3 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask Nia…"
                data-testid="ai-input"
                className="flex-1 bg-transparent border border-white/10 rounded-full px-4 py-2 text-sm placeholder:text-white/30 focus:outline-none focus:border-magenta/60"
              />
              <button onClick={send} disabled={loading} className="btn-magenta h-9 w-9 rounded-full flex items-center justify-center disabled:opacity-50" data-testid="ai-send">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
